/**
 * Firestore — RFQ agentic workflow (buyer + supplier functions).
 *
 * Database   : default
 * Collection : /workflows/rfq-agent/projects/{wid}
 *
 * Ported from reactjs/src/lib/firestore-rfq.ts. Buyer-side project functions
 * use the full RFQProject shape (modes/agentic-workflows/rfq/types.ts); the
 * supplier portal consumes the lighter RFQProjectMin.
 *
 * Rules: see reactjs/firestore.rules -> match /workflows/{workflowSlug}/projects/{projectId}
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  type Firestore,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirebaseFirestore } from "@/platform/firebase/client";
import { storage } from "@/platform/firebase/storage";
import { logAuditEntry as sharedLogAudit, getAuditLog as sharedGetAuditLog } from "./shared-workflows";
import { canTransitionTo } from "@/modes/agentic-workflows/rfq/state-machine";
import type {
  RFQProject,
  WorkflowState,
  IndustryVertical,
  StakeholdersData,
} from "@/modes/agentic-workflows/rfq/types";
import type { AuditEntry } from "@/modes/agentic-workflows/shared";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function db(): Firestore { return getFirebaseFirestore(); }
function projectCol() { return collection(db(), "workflows", "rfq-agent", "projects"); }
function projectDoc(wid: string) { return doc(db(), "workflows", "rfq-agent", "projects", wid); }
function quotesCol(wid: string) { return collection(db(), "workflows", "rfq-agent", "projects", wid, "quotes"); }
function questionsCol(wid: string) { return collection(db(), "workflows", "rfq-agent", "projects", wid, "questions"); }
function negotiationsCol(wid: string) { return collection(db(), "workflows", "rfq-agent", "projects", wid, "negotiations"); }
function evaluatorScoresCol(wid: string) { return collection(db(), "workflows", "rfq-agent", "projects", wid, "evaluator_scores"); }

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RFQSupplier {
  email: string;
  name: string;
  company?: string;
  token: string;
  invited_at?: string;
  status?: "invited" | "viewed" | "submitted";
}

export interface RFQProjectMin {
  wid: string;
  project_name: string;
  workflow_state: string;
  created_by_email?: string;
  industry_vertical?: string;
  updated_at?: string;
  step_07_publish_rfq?: {
    qa_deadline?: string;
    quote_deadline?: string;
    proposal_deadline?: string;
    suppliers?: RFQSupplier[];
    published_at?: string;
  };
  step_14_award_decision?: {
    recommended_supplier_email?: string;
    recommended_supplier_name?: string;
    approved?: boolean;
    approved_at?: string;
  };
}

export interface RFQQuestion {
  id: string;
  project_id: string;
  supplier_email: string;
  question_text: string;
  asked_at: string;
  status: "pending" | "answered";
  answer_text?: string;
  answered_at?: string;
  answered_by?: string;
  visibility?: "public" | "private";
}

export interface RFQQuote {
  // Old format fields (backward compatibility)
  company_name?: string;
  contact_person?: string;
  phone?: string;
  pricing?: string;
  pricing_notes?: string;
  certifications?: string[];
  terms_accurate?: boolean;
  terms_agree?: boolean;
  file_url?: string;
  file_name?: string;

  // New schema-driven format fields
  supplier_email?: string;
  rfq_id?: string;
  supplier_id?: string;
  industry_vertical?: string;
  line_items?: any[];
  section_responses?: any[];
  sections?: Record<string, any>;
  documents?: any[];
  qa_questions?: any[];
  calculated_total?: string | number;
  total_price?: string | number;
  meta?: {
    quote_reference?: string;
    submitted_by?: string;
    validity_days?: number;
    general_remarks?: string;
    deviations_summary?: string;
  };

  // Negotiation data (mirrored to quote doc for supplier access)
  negotiation?: any;

  // Status fields
  submitted_at?: string;
  approved_at?: string;
  approved_by?: string;
  status: "draft" | "submitted" | "approved" | "rejected";
  last_updated?: string;

  // Review decision (owner action on the quote in Step 9)
  review_decision?: "approved" | "rejected" | "rejected_resubmit";
  rejection_reason?: string;
  rejected_at?: string;
  rejected_by?: string;
  resubmit_requested_at?: string;

  // Email parsing fields
  source?: string;
  requires_manual_review?: boolean;
  confidence_scores?: any;
}

export interface EvaluatorEvaluation {
  id?: string;
  evaluator_email: string;
  evaluator_name: string;
  evaluator_weight: number;
  status: "not_started" | "in_progress" | "completed";
  quotes_evaluated: number;
  total_quotes: number;
  scores: Record<string, any>; // supplier_email -> scores
  submitted_at?: string;
  updated_at: string;
}

// ─── Audit logging ────────────────────────────────────────────────────────────

export async function logAuditEntry(
  projectId: string,
  action: string,
  performedBy: string,
  performedByEmail: string,
  fromState?: WorkflowState,
  toState?: WorkflowState,
  details?: Record<string, any>,
): Promise<void> {
  await sharedLogAudit("rfq-agent", projectId, {
    project_id: projectId,
    action,
    performed_by: performedBy,
    performed_by_email: performedByEmail,
    performed_at: new Date().toISOString(),
    ...(fromState !== undefined ? { from_state: fromState } : {}),
    ...(toState !== undefined ? { to_state: toState } : {}),
    ...(details !== undefined ? { details } : {}),
  });
}

export { sharedGetAuditLog as getAuditLog };

// ─── Project CRUD ─────────────────────────────────────────────────────────────

export async function createRFQProject(
  userId: string,
  userEmail: string,
  projectName: string,
  industry: IndustryVertical,
): Promise<string> {
  const wid = `rfq-${Date.now()}`;
  const ref = projectDoc(wid);

  const now = new Date().toISOString();
  const newProject: Partial<RFQProject> = {
    wid,
    workflow_slug: "rfq-agent",
    project_name: projectName,
    industry_vertical: industry,
    created_by: userId,
    created_by_email: userEmail,
    created_at: now,
    updated_at: now,
    workflow_state: "INITIATED",
    phase_locks: {
      phase_1_locked: false,
      phase_2_locked: false,
      phase_3_locked: false,
    },
  };

  await setDoc(ref, newProject);
  await logAuditEntry(wid, "project_created", userId, userEmail, undefined, "INITIATED");

  return wid;
}

/** Full project shape for the buyer-side workflow. */
export async function getFullRFQProject(wid: string): Promise<RFQProject | null> {
  const ref = projectDoc(wid);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as RFQProject) : null;
}

/** Lightweight project shape for the supplier portal. */
export async function getRFQProject(wid: string): Promise<RFQProjectMin | null> {
  const snap = await getDoc(projectDoc(wid));
  return snap.exists() ? (snap.data() as RFQProjectMin) : null;
}

export async function getUserRFQProjects(userId: string): Promise<RFQProject[]> {
  const ref = projectCol();
  const q = query(ref, where("created_by", "==", userId), orderBy("updated_at", "desc"), limit(50));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as RFQProject);
}

export async function getUserRFQProjectsWhereStakeholder(userEmail: string): Promise<RFQProject[]> {
  const ref = projectCol();
  const snap = await getDocs(ref);

  const results: RFQProject[] = [];
  snap.docs.forEach((d) => {
    const data = d.data() as RFQProject;
    const stakeholders = data.step_04_stakeholders as StakeholdersData | undefined;
    if (
      stakeholders?.evaluator_emails?.includes(userEmail) ||
      stakeholders?.approver_emails?.includes(userEmail) ||
      stakeholders?.observer_emails?.includes(userEmail) ||
      stakeholders?.supplier_emails?.includes(userEmail)
    ) {
      results.push(data);
    }
  });
  return results;
}

export async function getUserRFQProjectsAsSupplier(userEmail: string): Promise<RFQProjectMin[]> {
  const ref = projectCol();
  const snap = await getDocs(ref);

  const results: RFQProjectMin[] = [];
  snap.docs.forEach((d) => {
    const data = d.data() as RFQProjectMin;
    const suppliers = data.step_07_publish_rfq?.suppliers;
    if (suppliers?.some((s) => s.email === userEmail)) {
      results.push(data);
    }
  });
  return results;
}

export async function updateRFQData(
  wid: string,
  path: string,
  data: any,
): Promise<void> {
  const ref = projectDoc(wid);

  if (path === "_batch" && typeof data === "object") {
    await updateDoc(ref, {
      ...data,
      updated_at: new Date().toISOString(),
    });
  } else {
    await updateDoc(ref, {
      [path]: data,
      updated_at: new Date().toISOString(),
    });
  }
}

export async function transitionWorkflowState(
  wid: string,
  newState: WorkflowState,
  userId: string,
  userEmail: string,
): Promise<void> {
  const project = await getFullRFQProject(wid);
  if (!project) throw new Error("Project not found");

  const currentState = project.workflow_state;

  if (currentState === newState) return;

  if (!canTransitionTo(currentState, newState)) {
    throw new Error(`Invalid transition from ${currentState} to ${newState}`);
  }

  const ref = projectDoc(wid);
  await updateDoc(ref, {
    workflow_state: newState,
    updated_at: new Date().toISOString(),
  });

  await logAuditEntry(wid, "state_transition", userId, userEmail, currentState, newState);
}

export async function lockPhase(
  wid: string,
  phaseNum: 1 | 2 | 3,
  userId: string,
  userEmail: string,
): Promise<void> {
  const ref = projectDoc(wid);
  const lockField = `phase_locks.phase_${phaseNum}_locked`;

  await updateDoc(ref, {
    [lockField]: true,
    "phase_locks.locked_at": new Date().toISOString(),
    "phase_locks.locked_by": userId,
    updated_at: new Date().toISOString(),
  });

  await logAuditEntry(wid, `phase_${phaseNum}_locked`, userId, userEmail);
}

export async function cancelRFQ(
  wid: string,
  reason: string,
  userId: string,
  userEmail: string,
): Promise<void> {
  const ref = projectDoc(wid);
  await updateDoc(ref, {
    workflow_state: "CANCELLED",
    cancellation_reason: reason,
    cancelled_at: new Date().toISOString(),
    cancelled_by: userId,
    updated_at: new Date().toISOString(),
  });

  await logAuditEntry(wid, "rfq_cancelled", userId, userEmail, undefined, "CANCELLED", { reason });
}

export async function reopenEvaluation(
  wid: string,
  reason: string,
  userId: string,
  userEmail: string,
): Promise<void> {
  const ref = projectDoc(wid);
  await updateDoc(ref, {
    workflow_state: "EVALUATION_REOPENED",
    reopen_reason: reason,
    reopened_at: new Date().toISOString(),
    reopened_by: userId,
    updated_at: new Date().toISOString(),
  });

  await logAuditEntry(wid, "evaluation_reopened", userId, userEmail, undefined, "EVALUATION_REOPENED", { reason });
}

export async function deleteRFQProject(wid: string): Promise<void> {
  const ref = projectDoc(wid);
  await deleteDoc(ref);
}

export async function rollbackToStep(
  wid: string,
  targetStep: number,
  userId: string,
  userEmail: string,
): Promise<void> {
  const project = await getFullRFQProject(wid);
  if (!project) throw new Error("Project not found");

  // Clear step data for steps after the target step (updateDoc null = delete field)
  const updates: Record<string, any> = {
    updated_at: new Date().toISOString(),
  };

  if (targetStep < 2) updates.step_02_requirements = null;
  if (targetStep < 3) updates.step_03_criteria = null;
  if (targetStep < 4) updates.step_04_stakeholders = null;
  if (targetStep < 5) updates.step_05_generate_rfq = null;
  if (targetStep < 6) updates.step_06_internal_approval = null;
  if (targetStep < 7) updates.step_07_publish_rfq = null;
  if (targetStep < 8) updates.step_08_qa_management = null;
  if (targetStep < 9) updates.step_09_quote_review = null;
  if (targetStep < 10) updates.step_10_negotiation = null;
  if (targetStep < 11) updates.step_11_finalize_quotes = null;
  if (targetStep < 12) updates.step_12_evaluation = null;
  if (targetStep < 13) updates.step_13_consensus = null;
  if (targetStep < 14) updates.step_14_award_decision = null;
  if (targetStep < 15) updates.step_15_notification = null;

  // Update state based on target step
  const stateMap: Record<number, WorkflowState> = {
    1: "INITIATED",
    2: "REQUIREMENTS_DEFINED",
    3: "CRITERIA_DEFINED",
    4: "STAKEHOLDERS_IDENTIFIED",
    5: "RFQ_DRAFTED",
    6: "RFQ_APPROVED",
    7: "RFQ_PUBLISHED",
    8: "QNA_CLOSED",
    9: "QUOTES_UNDER_REVIEW",
    10: "NEGOTIATION_IN_PROGRESS",
    11: "QUOTES_FINALIZED",
    12: "EVALUATION_IN_PROGRESS",
    13: "CONSENSUS_CALCULATED",
    14: "AWARD_DECISION_PENDING",
    15: "SUPPLIERS_NOTIFIED",
  };

  updates.workflow_state = stateMap[targetStep] || "INITIATED";

  const ref = projectDoc(wid);
  await updateDoc(ref, updates);

  await logAuditEntry(wid, "rollback", userId, userEmail, project.workflow_state, updates.workflow_state, {
    target_step: targetStep,
  });
}

// ─── Token validation ─────────────────────────────────────────────────────────

export async function validateSupplierToken(wid: string, token: string): Promise<boolean> {
  try {
    const project = await getRFQProject(wid);
    if (!project) return false;
    return (project.step_07_publish_rfq?.suppliers ?? []).some((s) => s.token === token);
  } catch { return false; }
}

export async function getSupplierFromToken(
  wid: string,
  token: string,
): Promise<{ email: string; name: string; company: string } | null> {
  const project = await getRFQProject(wid);
  if (!project) return null;
  const supplier = (project.step_07_publish_rfq?.suppliers ?? []).find((s) => s.token === token);
  return supplier
    ? { email: supplier.email, name: supplier.name, company: supplier.company ?? supplier.name }
    : null;
}

export async function linkSupplierToken(
  wid: string,
  _token: string,
  _userId: string,
  _userEmail: string,
): Promise<void> {
  // Token is already validated; no additional write needed for RFQ
  void wid;
}

// ─── Q&A ──────────────────────────────────────────────────────────────────────

export async function getAllQuestions(wid: string): Promise<RFQQuestion[]> {
  try {
    const q = query(questionsCol(wid), orderBy("asked_at", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as RFQQuestion));
  } catch { return []; }
}

export async function getAnsweredQuestions(wid: string): Promise<RFQQuestion[]> {
  try {
    const q = query(questionsCol(wid), where("status", "==", "answered"), orderBy("answered_at", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as RFQQuestion));
  } catch { return []; }
}

export async function submitQuestion(
  wid: string,
  supplierEmail: string,
  questionText: string,
): Promise<void> {
  await addDoc(questionsCol(wid), {
    project_id: wid,
    supplier_email: supplierEmail,
    question_text: questionText,
    asked_at: new Date().toISOString(),
    status: "pending",
  });
}

export async function answerQuestion(
  wid: string,
  questionId: string,
  answerText: string,
  answeredBy: string,
  visibility: "public" | "private" = "public",
): Promise<void> {
  const questionRef = doc(db(), `workflows/rfq-agent/projects/${wid}/questions`, questionId);

  if (!answerText) {
    await updateDoc(questionRef, { visibility });
    return;
  }

  await updateDoc(questionRef, {
    answer_text: answerText,
    answered_at: new Date().toISOString(),
    answered_by: answeredBy,
    status: "answered",
    visibility,
  });
}

// ─── Quotes ───────────────────────────────────────────────────────────────────

export async function getSupplierQuote(wid: string, supplierEmail: string): Promise<RFQQuote | null> {
  const snap = await getDoc(doc(quotesCol(wid), supplierEmail));
  return snap.exists() ? (snap.data() as RFQQuote) : null;
}

export async function saveQuote(
  wid: string,
  supplierEmail: string,
  quote: Omit<RFQQuote, "status" | "supplier_email">,
  status: "draft" | "submitted",
): Promise<void> {
  const quoteRef = doc(quotesCol(wid), supplierEmail);
  await setDoc(
    quoteRef,
    {
      ...quote,
      status,
      supplier_email: supplierEmail,
      last_updated: new Date().toISOString(),
      ...(status === "submitted" ? { submitted_at: new Date().toISOString() } : {}),
    },
    { merge: true },
  );
}

export async function getAllQuotes(wid: string): Promise<RFQQuote[]> {
  try {
    const snap = await getDocs(quotesCol(wid));
    return snap.docs.map((d) => d.data() as RFQQuote);
  } catch {
    console.error("[getAllQuotes] Error fetching all quotes");
    return [];
  }
}

export async function getSupplierResponse(wid: string, supplierEmail: string): Promise<any | null> {
  return getSupplierQuote(wid, supplierEmail);
}

/** Calculate total from line items by finding price-related fields. */
function calculateTotalFromLineItems(lineItems: any[]): number {
  if (!lineItems || lineItems.length === 0) {
    const randomTotal = Math.floor(Math.random() * 50000) + 10000; // $10k - $60k
    console.log(`[calculateTotal] No line items, using random total: ${randomTotal}`);
    return randomTotal;
  }

  let total = 0;
  const priceKeywords = ["rate", "price", "cost", "amount", "fee", "charge"];

  for (const item of lineItems) {
    if (!item.fields) continue;

    for (const [key, value] of Object.entries(item.fields)) {
      const keyLower = key.toLowerCase();
      const isPriceField = priceKeywords.some((keyword) => keyLower.includes(keyword));

      if (isPriceField && value) {
        const numValue = parseFloat(value as string);
        if (!isNaN(numValue)) {
          total += numValue;
        }
      }
    }
  }

  if (total === 0) {
    const randomTotal = Math.floor(Math.random() * 50000) + 10000;
    console.log(`[calculateTotal] No price fields found, using random total: ${randomTotal}`);
    return randomTotal;
  }

  return total;
}

export async function saveSupplierResponse(
  wid: string,
  supplierEmail: string,
  response: any,
  status: "draft" | "submitted",
): Promise<void> {
  const quoteDoc = doc(quotesCol(wid), supplierEmail);

  const responseData: any = {};
  for (const key in response) {
    if (key !== "status") {
      responseData[key] = response[key];
    }
  }

  const calculatedTotal = calculateTotalFromLineItems(response.line_items || []);
  responseData.calculated_total = calculatedTotal;

  responseData.status = status;
  responseData.supplier_email = supplierEmail;
  responseData.last_updated = new Date().toISOString();

  if (status === "submitted") {
    responseData.submitted_at = new Date().toISOString();
    // Clear any previous review decision so a resubmitted quote starts fresh.
    responseData.review_decision = null;
    responseData.rejection_reason = null;
  }

  await setDoc(quoteDoc, responseData, { merge: true });
}

// ─── File upload ──────────────────────────────────────────────────────────────

export async function uploadQuoteFile(
  wid: string,
  supplierEmail: string,
  file: File,
): Promise<{ url: string; name: string }> {
  const filePath = `rfq-quotes/${wid}/${supplierEmail}/${Date.now()}-${file.name}`;
  const storageRef = ref(storage, filePath);
  const snapshot = await uploadBytes(storageRef, file);
  const url = await getDownloadURL(snapshot.ref);
  return { url, name: file.name };
}

/** Generate a fresh download URL for a GCS file. */
export async function getFreshDownloadURL(gcsPath: string): Promise<string> {
  try {
    const storageRef = ref(storage, gcsPath);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("[getFreshDownloadURL] Error generating download URL:", error);
    throw error;
  }
}

// ─── Negotiation (Step 10) ────────────────────────────────────────────────────

/** Calculate total amount from a quote document. */
function parseAmount(value: any): number {
  if (value === undefined || value === null) return NaN;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/[^0-9.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? NaN : parsed;
}

function calculateQuoteTotal(quote: any): number {
  if (quote.calculated_total !== undefined && quote.calculated_total !== null) {
    const total = parseAmount(quote.calculated_total);
    if (!isNaN(total) && total > 0) return total;
  }

  if (quote.line_items && Array.isArray(quote.line_items)) {
    const total = calculateTotalFromLineItems(quote.line_items);
    if (total > 0) return total;
  }

  if (quote.sections) {
    if (quote.sections.rates && quote.sections.rates.total_price) {
      const price = parseAmount(quote.sections.rates.total_price);
      if (!isNaN(price) && price > 0) return price;
    }

    for (const [sectionKey, section] of Object.entries(quote.sections) as any[]) {
      if (section && section.total_price) {
        const price = parseAmount(section.total_price);
        if (!isNaN(price) && price > 0) return price;
      }
    }
  }

  if (quote.total_price) {
    const price = parseAmount(quote.total_price);
    if (!isNaN(price) && price > 0) return price;
  }

  // Generic-parsed quotes store the total in `pricing` (e.g. "3000" or "$3,000")
  if (quote.pricing) {
    const price = parseAmount(quote.pricing);
    if (!isNaN(price) && price > 0) return price;
  }

  console.warn("[calculateQuoteTotal] Could not calculate total, returning 0");
  return 0;
}

/**
 * Initialize negotiations from approved quotes.
 * Creates negotiation documents for all approved quotes.
 */
export async function initializeNegotiations(wid: string): Promise<void> {
  console.log("[initializeNegotiations] Starting for wid:", wid);

  const quotes = await getAllQuotes(wid);
  const approvedQuotes = quotes.filter((q) => q.status === "approved");

  if (approvedQuotes.length === 0) {
    console.warn("[initializeNegotiations] No approved quotes found");
    return;
  }

  const project = await getFullRFQProject(wid);
  if (!project) throw new Error("Project not found");

  const suppliers = project.step_07_publish_rfq?.suppliers || [];
  const quoteDeadline = project.step_07_publish_rfq?.quote_deadline;
  const negotiationPeriodDays = project.step_07_publish_rfq?.negotiation_period_days || 7;
  const negotiationDeadline = quoteDeadline
    ? new Date(new Date(quoteDeadline).getTime() + negotiationPeriodDays * 86400000).toISOString()
    : new Date(Date.now() + negotiationPeriodDays * 86400000).toISOString();

  for (const quote of approvedQuotes) {
    const email = quote.supplier_email as string;
    const quoteTotal = calculateQuoteTotal(quote);

    const supplier = suppliers.find((s) => s.email === email);
    const supplierName = supplier?.name || quote.meta?.submitted_by || email || "Unknown";

    const negotiation = {
      supplier_email: email,
      supplier_name: supplierName,
      status: "active",
      initial_quote_amount: quoteTotal,
      current_quote_amount: quoteTotal,
      final_quote_amount: quoteTotal,
      improvement_percentage: 0,
      negotiation_deadline: negotiationDeadline,
      rounds: [
        {
          round_number: 1,
          type: "supplier_offer",
          amount: quoteTotal,
          message: "Initial quote submitted",
          timestamp: quote.submitted_at || new Date().toISOString(),
        },
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Write to negotiations subcollection (for owner access)
    const negotiationDoc = doc(negotiationsCol(wid), email);
    await setDoc(negotiationDoc, negotiation);

    // Also write to quote document (for supplier access)
    const quoteDoc = doc(quotesCol(wid), email);
    await updateDoc(quoteDoc, { negotiation });
  }

  console.log("[initializeNegotiations] Initialized", approvedQuotes.length, "negotiations");
}

/**
 * Repair negotiations whose amounts are 0/missing (e.g. initialized before the
 * quote total was available). Re-derives totals from the approved quotes and
 * patches both the negotiations subcollection and the quote documents.
 */
export async function repairNegotiationAmounts(wid: string): Promise<void> {
  try {
    const quotes = await getAllQuotes(wid);
    const approvedMap = new Map<string, any>();
    quotes.forEach((q) => {
      if (q.status === "approved" && q.supplier_email) {
        approvedMap.set(q.supplier_email, q);
      }
    });

    const negotiations = await getNegotiations(wid);
    if (negotiations.length === 0) return;

    let repairedAny = false;

    for (const neg of negotiations) {
      const email = neg.supplier_email || neg.id;
      const quote = approvedMap.get(email);
      const total = quote ? calculateQuoteTotal(quote) : 0;
      if (!(total > 0)) continue;

      const needsRepair =
        !(neg.initial_quote_amount > 0) ||
        !(neg.current_quote_amount > 0) ||
        (neg.rounds || []).some(
          (r: any) => r.type === "supplier_offer" && (r.amount === undefined || r.amount === null || r.amount === 0)
        );

      if (!needsRepair) continue;

      repairedAny = true;

      const repaired: any = {
        improvement_percentage: 0,
        updated_at: new Date().toISOString(),
      };
      if (!(neg.initial_quote_amount > 0)) repaired.initial_quote_amount = total;
      if (!(neg.current_quote_amount > 0)) repaired.current_quote_amount = total;
      if (!(neg.final_quote_amount > 0)) repaired.final_quote_amount = total;

      const rounds = (neg.rounds || []).map((r: any) => {
        if (r.type === "supplier_offer" && (r.amount === undefined || r.amount === null || r.amount === 0)) {
          return { ...r, amount: total };
        }
        return r;
      });
      repaired.rounds = rounds;

      const negotiationRef = doc(negotiationsCol(wid), email);
      const snap = await getDoc(negotiationRef);
      if (snap.exists()) {
        await updateDoc(negotiationRef, repaired);
      } else {
        await setDoc(negotiationRef, {
          supplier_email: email,
          supplier_name: neg.supplier_name || email,
          status: "active",
          initial_quote_amount: total,
          current_quote_amount: total,
          final_quote_amount: total,
          improvement_percentage: 0,
          rounds,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }

      const quoteDoc = doc(quotesCol(wid), email);
      const quoteSnap = await getDoc(quoteDoc);
      if (quoteSnap.exists()) {
        const quoteData = quoteSnap.data();
        const existingNegotiation = quoteData.negotiation || {
          status: "active",
          initial_quote_amount: quoteData.calculated_total || total,
          current_quote_amount: quoteData.calculated_total || total,
          final_quote_amount: quoteData.calculated_total || total,
          improvement_percentage: 0,
          rounds: [],
        };
        await updateDoc(quoteDoc, {
          negotiation: {
            ...existingNegotiation,
            ...repaired,
            rounds,
            status: "active",
          },
        });
      }
    }

    if (repairedAny) {
      console.log("[repairNegotiationAmounts] Repaired negotiation amounts for", wid);
    }
  } catch (error) {
    console.error("[repairNegotiationAmounts] Error:", error);
  }
}

/**
 * Get all negotiations for a project, merging data from both the
 * negotiations subcollection AND quote documents.
 */
export async function getNegotiations(wid: string): Promise<any[]> {
  try {
    const snapshot = await getDocs(negotiationsCol(wid));
    const negotiations = snapshot.docs.map((d) => ({ ...d.data(), id: d.id })) as any[];

    const quotesSnapshot = await getDocs(quotesCol(wid));
    const quoteNegotiations: any[] = [];
    quotesSnapshot.forEach((d) => {
      const quoteData = d.data();
      if (quoteData.negotiation && quoteData.negotiation.rounds && quoteData.negotiation.rounds.length > 0) {
        quoteNegotiations.push({
          ...quoteData.negotiation,
          id: d.id,
          source: "quote_document",
        });
      }
    });

    const supplierEmails = new Set<string>([
      ...negotiations.map((n: any) => n.supplier_email || n.id),
      ...quoteNegotiations.map((n: any) => n.supplier_email || n.id),
    ]);

    const mergedNegotiations: any[] = [];

    for (const email of supplierEmails) {
      const subcolNeg = negotiations.find((n: any) => (n.supplier_email || n.id) === email);
      const quoteNeg = quoteNegotiations.find((n: any) => (n.supplier_email || n.id) === email);

      if (subcolNeg && quoteNeg) {
        const existingRoundNumbers = new Set(subcolNeg.rounds?.map((r: any) => `${r.round_number}-${r.type}`) || []);
        const additionalRounds = (quoteNeg.rounds || []).filter(
          (r: any) => !existingRoundNumbers.has(`${r.round_number}-${r.type}`)
        );

        mergedNegotiations.push({
          ...subcolNeg,
          rounds: [...(subcolNeg.rounds || []), ...additionalRounds],
          current_quote_amount: quoteNeg.current_quote_amount || subcolNeg.current_quote_amount,
          final_quote_amount: quoteNeg.final_quote_amount || subcolNeg.final_quote_amount,
          improvement_percentage: quoteNeg.improvement_percentage || subcolNeg.improvement_percentage,
        });
      } else if (quoteNeg && !subcolNeg) {
        mergedNegotiations.push(quoteNeg);
      } else if (subcolNeg) {
        mergedNegotiations.push(subcolNeg);
      }
    }

    return mergedNegotiations;
  } catch (error) {
    console.error("[getNegotiations] Error:", error);
    throw error;
  }
}

export async function getNegotiation(
  wid: string,
  supplierEmail: string,
): Promise<any | null> {
  try {
    const negotiationRef = doc(negotiationsCol(wid), supplierEmail);
    const snapshot = await getDoc(negotiationRef);
    return snapshot.exists() ? { ...snapshot.data(), id: snapshot.id } : null;
  } catch (error) {
    console.error("[getNegotiation] Error:", error);
    throw error;
  }
}

/** Get negotiation for a specific supplier from their quote document. */
export async function getSupplierNegotiation(
  wid: string,
  supplierEmail: string,
): Promise<any | null> {
  try {
    const snapshot = await getDoc(doc(quotesCol(wid), supplierEmail));
    if (snapshot.exists()) {
      const data = snapshot.data();
      if (data.negotiation) {
        return { ...data.negotiation, id: supplierEmail };
      }
    }
    return null;
  } catch (error) {
    console.error("[getSupplierNegotiation] Error:", error);
    throw error;
  }
}

/**
 * Add negotiation round — writes to BOTH the negotiations subcollection
 * (for owner) AND the quote document (for supplier access).
 */
export async function addNegotiationRound(
  wid: string,
  supplierEmail: string,
  round: any,
): Promise<void> {
  console.log("[addNegotiationRound] Adding round:", { wid, supplierEmail, round });

  let currentAmount: number;
  let improvementPercentage: number;

  try {
    const negotiationRef = doc(negotiationsCol(wid), supplierEmail);
    const snapshot = await getDoc(negotiationRef);

    let negotiation: any;

    if (snapshot.exists()) {
      negotiation = snapshot.data();
      const rounds = negotiation.rounds || [];
      rounds.push(round);

      currentAmount = negotiation.current_quote_amount;
      if (round.type === "buyer_counter" && round.amount) {
        currentAmount = round.amount;
      } else if (round.type === "supplier_offer" && round.amount) {
        currentAmount = round.amount;
      }

      improvementPercentage = negotiation.initial_quote_amount > 0
        ? ((negotiation.initial_quote_amount - currentAmount) / negotiation.initial_quote_amount) * 100
        : 0;

      await updateDoc(negotiationRef, {
        rounds,
        current_quote_amount: currentAmount,
        final_quote_amount: currentAmount,
        improvement_percentage: improvementPercentage,
        updated_at: new Date().toISOString(),
      });
    } else {
      currentAmount = round.amount || 0;
      improvementPercentage = 0;
      negotiation = {
        supplier_email: supplierEmail,
        status: "active",
        initial_quote_amount: currentAmount,
        current_quote_amount: currentAmount,
        final_quote_amount: currentAmount,
        improvement_percentage: improvementPercentage,
        rounds: [round],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      await setDoc(negotiationRef, negotiation);
    }

    console.log("[addNegotiationRound] Updated negotiations subcollection");
  } catch (subcollectionError) {
    console.warn(
      "[addNegotiationRound] Could not update negotiations subcollection (likely permission denied for supplier):",
      subcollectionError
    );
    currentAmount = round.amount || 0;
    improvementPercentage = 0;
  }

  // Also update quote document so supplier can access it
  const quoteDoc = doc(quotesCol(wid), supplierEmail);
  const quoteSnapshot = await getDoc(quoteDoc);

  if (quoteSnapshot.exists()) {
    const quoteData = quoteSnapshot.data();
    const existingNegotiation = quoteData.negotiation || {
      status: "active",
      initial_quote_amount: quoteData.calculated_total || currentAmount,
      current_quote_amount: quoteData.calculated_total || currentAmount,
      improvement_percentage: 0,
      rounds: [],
    };

    const existingRounds = existingNegotiation.rounds || [];
    existingRounds.push(round);

    await updateDoc(quoteDoc, {
      negotiation: {
        ...existingNegotiation,
        rounds: existingRounds,
        current_quote_amount: currentAmount,
        final_quote_amount: currentAmount,
        improvement_percentage: improvementPercentage,
        updated_at: new Date().toISOString(),
        status: "active",
      },
    });
  }

  console.log("[addNegotiationRound] Round added successfully to both locations");
}

export async function updateNegotiationStatus(
  wid: string,
  supplierEmail: string,
  status: "active" | "finalized" | "withdrawn",
  userId: string,
): Promise<void> {
  console.log("[updateNegotiationStatus] Updating:", { wid, supplierEmail, status });

  const updates: Record<string, any> = {
    status,
    updated_at: new Date().toISOString(),
  };

  if (status === "finalized" || status === "withdrawn") {
    updates.finalized_at = new Date().toISOString();
    updates.finalized_by = userId;
  }

  const negotiationRef = doc(negotiationsCol(wid), supplierEmail);
  try {
    await updateDoc(negotiationRef, updates);
  } catch (subError: any) {
    if (subError.code === "not-found") {
      console.log("[updateNegotiationStatus] Negotiation subcollection document not found, skipping");
    } else {
      throw subError;
    }
  }

  const quoteRef = doc(quotesCol(wid), supplierEmail);
  try {
    const quoteDoc = await getDoc(quoteRef);
    if (quoteDoc.exists() && quoteDoc.data().negotiation) {
      await updateDoc(quoteRef, {
        "negotiation.status": status,
        "negotiation.updated_at": updates.updated_at,
        ...(updates.finalized_at && { "negotiation.finalized_at": updates.finalized_at }),
        ...(updates.finalized_by && { "negotiation.finalized_by": updates.finalized_by }),
      });
    }
  } catch (quoteError: any) {
    console.log("[updateNegotiationStatus] Could not update quote document:", quoteError.message);
  }
}

export async function areAllNegotiationsFinalized(wid: string): Promise<boolean> {
  try {
    const negotiations = await getNegotiations(wid);
    if (negotiations.length === 0) return false;
    return negotiations.every((n) => n.status === "finalized" || n.status === "withdrawn");
  } catch (error) {
    console.error("[areAllNegotiationsFinalized] Error:", error);
    throw error;
  }
}

// ─── Evaluator evaluation (Step 12) ───────────────────────────────────────────

export async function saveEvaluatorEvaluation(
  wid: string,
  evaluatorEmail: string,
  data: Partial<EvaluatorEvaluation>,
): Promise<void> {
  try {
    const evalRef = doc(evaluatorScoresCol(wid), evaluatorEmail);
    await setDoc(
      evalRef,
      {
        ...data,
        evaluator_email: evaluatorEmail,
        updated_at: new Date().toISOString(),
      },
      { merge: true },
    );
  } catch (error) {
    console.error("[saveEvaluatorEvaluation] Error:", error);
    throw error;
  }
}

export async function getEvaluatorEvaluations(wid: string): Promise<EvaluatorEvaluation[]> {
  try {
    const snap = await getDocs(evaluatorScoresCol(wid));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as EvaluatorEvaluation));
  } catch (error) {
    console.error("[getEvaluatorEvaluations] Error:", error);
    return [];
  }
}

export async function getEvaluatorEvaluation(
  wid: string,
  evaluatorEmail: string,
): Promise<EvaluatorEvaluation | null> {
  try {
    const snap = await getDoc(doc(evaluatorScoresCol(wid), evaluatorEmail));
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() } as EvaluatorEvaluation;
    }
    return null;
  } catch (error) {
    console.error("[getEvaluatorEvaluation] Error:", error);
    return null;
  }
}

export async function submitEvaluatorEvaluation(
  wid: string,
  evaluatorEmail: string,
  evaluatorName: string,
  evaluatorWeight: number,
  scores: Record<string, any>,
  totalQuotes: number,
): Promise<void> {
  try {
    const evalRef = doc(evaluatorScoresCol(wid), evaluatorEmail);
    await setDoc(evalRef, {
      evaluator_email: evaluatorEmail,
      evaluator_name: evaluatorName,
      evaluator_weight: evaluatorWeight,
      status: "completed",
      quotes_evaluated: Object.keys(scores).length,
      total_quotes: totalQuotes,
      scores,
      submitted_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[submitEvaluatorEvaluation] Error:", error);
    throw error;
  }
}
